import { Loader2 } from 'lucide-react';
import { PortalDsAlert, PortalDsEmptyState } from 'src/components/portal-design-system';
import {
  useGetActivityInsightsQuery,
  useGetActivityInsightsUsersQuery,
} from './endpoints/activity-insights.endpoints';
import { UsageInsightsFilterBar } from './components/usage-insights-filter-bar';
import { UsageInsightsFlagsSection } from './components/usage-insights-flags-section';
import { UsageInsightsMetricCard, UsageInsightsMetricGrid } from './components/usage-insights-metric-card';
import { UsageInsightsNarrativeSection } from './components/usage-insights-narrative-section';
import { UsageInsightsTransitionGraph } from './components/usage-insights-transition-graph';
import { UsageInsightsUsersTable } from './components/usage-insights-users-table';
import { useUsageInsightsUrlParams } from './hooks/use-usage-insights-url-params';
import {
  formatGenerationsPerDownload,
  formatMetricValue,
  formatRatePercent,
  getActivityInsightsErrorMessage,
  isActivityInsightsNotAuthorised,
  sortFlagsBySeverity,
} from './utils/usage-insights.utils';

const USERS_PAGE_SIZE = 20;

export function UsageInsightsDashboard() {
  const {
    from,
    to,
    workspaceId,
    page,
    setFrom,
    setTo,
    setWorkspaceId,
    setPage,
  } = useUsageInsightsUrlParams();

  const queryArgs = {
    from,
    to,
    ...(workspaceId ? { workspace_id: workspaceId } : {}),
  };

  const {
    data: insightsData,
    isLoading: isInsightsLoading,
    isError: isInsightsError,
    error: insightsError,
  } = useGetActivityInsightsQuery({
    ...queryArgs,
    narrative: false,
  });

  const {
    data: narrativeData,
  } = useGetActivityInsightsQuery({
    ...queryArgs,
    narrative: true,
  }, {
    skip: !insightsData,
  });

  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useGetActivityInsightsUsersQuery({
    ...queryArgs,
    page,
    page_size: USERS_PAGE_SIZE,
  }, {
    skip: !insightsData,
  });

  if (isInsightsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  if (isInsightsError && isActivityInsightsNotAuthorised(insightsError)) {
    return (
      <PortalDsEmptyState
        title="Not authorised"
        description="Usage insights are available to Invisibly staff only."
      />
    );
  }

  if (isInsightsError || !insightsData) {
    return (
      <PortalDsAlert variant="danger">
        {getActivityInsightsErrorMessage(insightsError)}
      </PortalDsAlert>
    );
  }

  const metrics = insightsData.metrics;
  const sortedFlags = sortFlagsBySeverity(insightsData.flags ?? []);
  const narrative = narrativeData?.narrative ?? null;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <UsageInsightsFilterBar
        from={from}
        to={to}
        workspaceId={workspaceId}
        onFromChange={setFrom}
        onToChange={setTo}
        onWorkspaceIdChange={setWorkspaceId}
      />

      <UsageInsightsMetricGrid>
        <UsageInsightsMetricCard
          label="Active users"
          value={formatMetricValue(metrics.active_users)}
        />
        <UsageInsightsMetricCard
          label="Total generations"
          value={formatMetricValue(metrics.total_generations)}
        />
        <UsageInsightsMetricCard
          label="Generations per download"
          value={formatGenerationsPerDownload(metrics.generations_per_download)}
        />
        <UsageInsightsMetricCard
          label="Failure rate"
          value={formatRatePercent(metrics.failure_rate)}
        />
        <UsageInsightsMetricCard
          label="Abandonment rate"
          value={formatRatePercent(metrics.abandonment_rate)}
        />
      </UsageInsightsMetricGrid>

      <UsageInsightsFlagsSection flags={sortedFlags} />

      {narrative ? <UsageInsightsNarrativeSection narrative={narrative} /> : null}

      <UsageInsightsTransitionGraph transitions={insightsData.transitions ?? []} />

      {isUsersLoading ? (
        <div className="flex items-center justify-center rounded-xl border border-[#EDEEF2] bg-white p-8">
          <Loader2 className="size-6 animate-spin text-[#2563EB]" />
        </div>
      ) : isUsersError ? (
        <PortalDsAlert variant="danger">
          Could not load the user list.
        </PortalDsAlert>
      ) : (
        <UsageInsightsUsersTable
          rows={usersData?.results ?? []}
          totalCount={usersData?.count ?? 0}
          page={page}
          pageSize={USERS_PAGE_SIZE}
          onPageChange={setPage}
          from={from}
          to={to}
          workspaceId={workspaceId || undefined}
        />
      )}
    </div>
  );
}
